import { EndPointDoc, Method } from '../apiref/apireftypes';
import { ApiRef } from '../fasades/apiref';
import { explanTemperature } from './explanTemperature';
import { explainTopP } from './explainTopP';
import { GenCodeCurl } from '../apiref/genCodeCurl';
import { GenCodeJavaScript } from '../apiref/genCodeJavaScript';
import { GenCodePython } from '../apiref/genCodePython';

function genExampleBody(stream: boolean): object
{
    return {
        "model"      : "Llama-3.1-8b",
        "messages"   : [
            {
              "role"      : "system",
              "content"   : "You are a usefull assistent, answer question briefly?"
            },
            {
                "role"    : "user",
                "content" : "Why is the sky blue?"
            }
        ],
        "stream"     : stream,
        "temperature": 0.7,
        "top_p"       : 0.95
    };
}

export function getResponse() : EndPointDoc
{
    let messages = ApiRef.getType("Message[]");

    let model = <>
        <p>The identifier of the language model to use for generating the response. This should correspond to a
           valid model <strong>name</strong> from your available models (e.g., Llama-3.1-8b, DeepSeek-R1-8b, etc.).</p>
        <p>It can also be models you have created.</p>
        </>

    let stream = <>
        <p>Controls whether the response is returned as a complete message or streamed incrementally as it's generated.</p>
        <ul>
            <li>false - Returns the complete response only after generation is finished</li>
            <li>true - Streams response chunks as they're generated, allowing for real-time display</li>
        </ul>
        </>

    const exampleUrl              = "v1/generate_response";
    const exampleBody             = genExampleBody(false);
    const exampleBodyStreaming    = genExampleBody(true);
    const genCurl                 = new GenCodeCurl(Method.Post, exampleUrl, exampleBody);
    const genPython               = new GenCodePython(Method.Post, exampleUrl, exampleBody);
    const genJavaScript           = new GenCodeJavaScript(Method.Post, exampleUrl, exampleBody);
    const genCurlStreaming        = new GenCodeCurl(Method.Post, exampleUrl, exampleBodyStreaming);
    const genPythonStreaming      = new GenCodePython(Method.Post, exampleUrl, exampleBodyStreaming);
    const genJavaScriptStreaming  = new GenCodeJavaScript(Method.Post, exampleUrl, exampleBodyStreaming);
    const codeCurl                = genCurl.generate();
    let codePython                = genPython.generateImport();
    codePython                   += genPython.generateRequest();
    codePython                   += genPython.readResponse();
    let codeJavaScript            = genJavaScript.generateRequest();
    codeJavaScript               += genJavaScript.readResponse();
    const codeCurlStreaming       = genCurlStreaming.generate();
    let codePythonStreaming       = genPythonStreaming.generateImport();
    codePythonStreaming          += genPythonStreaming.generateRequest();
    codePythonStreaming          += genPythonStreaming.readResponseStreaming();
    let codeJavaScriptStreaming   = genJavaScriptStreaming.generateRequest();
    codeJavaScriptStreaming      += genJavaScriptStreaming.readResponseStreaming();

    return {
        "title"          : "Generate Response",
        "method"         : Method.Post,
        "url"            : "v1/generate_response",
        "docurl"         : "generate-response",
        "description"    : <p>Send a structured list of input messages with text, and the model will generate the next message in the conversation.</p>,
        "seoDescription" : "Send a structured list of input messages with text, and the model will generate the next message in the conversation.",
        "parameters"  : [
            {
                "name"        : "model",
                "type"        : "string",
                "description" : model,
                "optional"    : false
            },
            {
                "name"        : "messages",
                "type"        : "Message[]",
                "description" : messages.description,
                "optional"    : false
            },
            {
                "name"        : "stream",
                "type"        : "boolean",
                "description" : stream,
                "optional"    : true,
                "default"     : "false"
            },
            {
                "name"        : "temperature",
                "type"        : "number",
                "description" : explanTemperature(),
                "optional"    : true,
                "default"     : "0.7"
            },
            {
                "name"        : "top_p",
                "type"        : "number",
                "description" : explainTopP(),
                "optional"    : true,
                "default"     : "0.95"
            }
        ],
        "examples" : [
            {
                "title"          : "standard",
                "url"            : exampleUrl,
                "body"           : exampleBody,
                "output"         : [{
                    "id" : "7",
                    "message" : {
                        "content" : "The sky appears blue because of a phenomenon called Rayleigh scattering, where sunlight scatters off tiny molecules of gases in the atmosphere, such as nitrogen and oxygen.",
                        "role" : "assistant"
                    },
                    "usage" : {
                        "duration_ms" : 1297,
                        "input_tokens" : 33,
                        "output_tokens" : 33
                    }
                }],
                "responseType"   : ApiRef.getType("GenerateResponse"),
                "codeCurl"       : codeCurl,
                "codePython"     : codePython,
                "codeJavaScript" : codeJavaScript
            },
            {
                "title"          : "streaming",
                "url"            : exampleUrl,
                "body"           : exampleBodyStreaming,
                "output"         : [
                    {
                        "message" : "Loading model llama-3.1-8b",
                        "type" : "info"
                    },
                    {
                        "message" : "Loading tokenizer",
                        "type" : "info"
                    },
                    {
                        "delta" : "The",
                        "type" : "delta"
                    },
                    {
                        "delta" : " sky",
                        "type" : "delta"
                    },
                    {
                        "delta" : " appears",
                        "type" : "delta"
                    },
                    {
                        "delta" : " blue",
                        "type" : "delta"
                    },
                    {
                        "_truncated" : true
                    },
                    {
                        "delta" : " oxygen",
                        "type" : "delta"
                    },
                    {
                        "delta" : ".",
                        "type" : "delta"
                    },
                    {
                        "stop_reason" : "end_turn",
                        "type" : "stop",
                        "usage" : {
                            "duration_ms" : 1396,
                            "input_tokens" : 33,
                            "output_tokens" : 35
                        }
                    }
                ],
                "responseType"   : ApiRef.getType("StreamingChunk[]"),
                "codeCurl"       : codeCurlStreaming,
                "codePython"     : codePythonStreaming,
                "codeJavaScript" : codeJavaScriptStreaming
            }
        ]
    };
}

